/**
 * チェックポイント(CP)データの型定義とアクセサ
 *
 * 実体は src/data/checkpoints.json。ビルド時に PWA バンドルへ含める。
 * CLI もこの型を共有する。
 */

/** 万葉歌碑のコンテンツ(CLAUDE.md §10) */
export interface Poem {
  /** 原文(万葉仮名) */
  original: string;
  /** 訓読 */
  reading: string;
  /** 現代語訳 */
  translation: string;
  /** 出典(巻・歌番号) */
  source: string;
  /** 作者(伝承含む) */
  author: string;
}

export interface Checkpoint {
  /** CP識別子。"01"〜"NN" の2桁ゼロ埋め。 */
  id: string;
  name: string;
  lat: number;
  lng: number;
  /** ジオフェンス半径(メートル) */
  geofence_m: number;
  /**
   * 合言葉のHMACハッシュ(base64url 先頭16文字)。
   * 平文の合言葉はバンドルに含めない — 含めるとJSを読むだけで
   * 全CPの合言葉が判明し、フォールバック手段が無意味になるため。
   * 平文は `npm run cli -- keywords` が出力する out/signage.local.json
   * (看板制作用・gitignore済み)にのみ存在する。
   * null の場合はそのCPで合言葉入力を受け付けない。
   */
  keyword_hash: string | null;
  /** 音声ファイルパス(バンドルルート相対)。未制作なら null。 */
  audio: string | null;
  /** 歌碑写真パス。未撮影なら null。 */
  photo: string | null;
  poem: Poem;
  /** 解説テキスト(音声の書き起こしを兼ねる) */
  description: string;
  /**
   * 他CPからの徒歩所要時間の下限(分)。キーは相手のCP id。
   * 未設定のペアは座標から推定する(geo.estimateMinWalkMinutes)。
   */
  min_walk_minutes?: Record<string, number>;
  /** 現地調査で確定した項目にのみ true。false の間は仮値。 */
  surveyed?: boolean;
}

export interface Goal {
  /** "all" または必要スタンプ数 */
  required_count: "all" | number;
}

export interface CheckpointData {
  event_id: string;
  checkpoints: Checkpoint[];
  goal: Goal;
}

/** 達成に必要なスタンプ数を解決する */
export function requiredCount(data: CheckpointData): number {
  return data.goal.required_count === "all"
    ? data.checkpoints.length
    : data.goal.required_count;
}

export function findCheckpoint(data: CheckpointData, id: string): Checkpoint | undefined {
  return data.checkpoints.find((cp) => cp.id === id);
}

/**
 * 合言葉の正規化。
 * 高齢者の手入力を想定し、表記ゆれを広めに吸収する:
 *   - NFKC 正規化(全角英数・半角カナを統一)
 *   - 空白類をすべて除去
 *   - カタカナ → ひらがな
 *   - 英字は小文字化
 * 長音符「ー」と濁点の有無は区別する(別語になり得るため)。
 */
export function normalizeKeyword(input: string): string {
  return input
    .normalize("NFKC")
    .replace(/\s+/gu, "")
    .replace(/[ァ-ヶ]/gu, (c) => String.fromCharCode(c.charCodeAt(0) - 0x60))
    .toLowerCase();
}
